import styled from 'styled-components';
import { TEAM_MEMBERS } from './TeamPanel';
import { useCalendarData } from './useCalendarData';
import { DAYS } from './days';
import {
  CalendarEvent,
  parseConcepts,
  getEventLinks,
  conceptColor,
  formatHour,
  roundToFiveMin,
} from './eventUtils';
import { EVENT_TIMES } from './eventTimes';
import { SUPPLEMENTS } from './scheduleSupplements';
import { COLORS, SPACE, FONT, BREAKPOINT } from './theme';

const ABOUT_COPY = `This is a fast-paced introduction to programming, designed to help you build foundational skills and confidently use resources, including AI, to create useful programs. This course was originally created for Stanford humanities faculty (who had no prior coding experience) to learn to code in two weeks! We met every day from 9am to 5pm for two weeks in June 2025. Each day involves short lessons, handouts, and coding assignments (the times listed are rough estimates). This website is now a resource for anyone who'd like a self-paced programming course, or instructors looking for inspiration. Enjoy!

Sierra & TJ`;

const RESOURCE_LINKS = [
  { label: 'Reference Sheet', href: 'https://docs.google.com/document/d/19dZkeZ4HPaLHIZ6o_HxU5PiUW_4V_wSfrHINP-18EI8/edit?usp=sharing' },
  { label: 'Codingbat', href: 'https://codingbat.com/python' },
  { label: 'Lecture Demo Code Snippets', href: 'https://docs.google.com/document/d/1bagiR5k0chShtYYlLEhdpuGBZbn4-lzo390KbE92K8Y/edit?usp=sharing' },
];

// Hand-curated time per day+id. Falls back to event.start (rounded) if no entry.
const resolveEventHour = (event: CalendarEvent, dayN: number): number => {
  const override = EVENT_TIMES[dayN]?.[event.id];
  if (typeof override === 'number') return override;
  return roundToFiveMin(event.start);
};

const EventLink = ({ href, children }: { href?: string; children: React.ReactNode }) => {
  if (!href) return null;
  return (
    <InlineLink href={href} target="_blank" rel="noopener noreferrer">
      {children}
    </InlineLink>
  );
};

const EventItem = ({ event, dayN }: { event: CalendarEvent; dayN: number }) => {
  const time = formatHour(resolveEventHour(event, dayN));

  if (event.type === 'break') {
    return (
      <BreakRow>
        <Time>{time}</Time>
        <BreakLabel>{event.title}</BreakLabel>
      </BreakRow>
    );
  }

  if (event.type === 'note') {
    return (
      <NoteRow>
        <Time>{time}</Time>
        <NoteLabel>{event.title}</NoteLabel>
      </NoteRow>
    );
  }

  const concepts = parseConcepts(event);
  const links = getEventLinks(event);

  return (
    <EventRow>
      <Time>{time}</Time>
      <EventBody>
        <EventTitleLine>
          <EventTitle>{event.title}</EventTitle>
          {concepts.map(c => (
            <Pill key={c} style={{ backgroundColor: conceptColor(c) }}>{c}</Pill>
          ))}
        </EventTitleLine>
      </EventBody>
      <EventLinks>
        <EventLink href={links.assignment}>Assignment</EventLink>
        <EventLink href={links.example}>Example</EventLink>
        <EventLink href={links.slides}>Slides</EventLink>
        <EventLink href={links.handout}>Handout</EventLink>
      </EventLinks>
    </EventRow>
  );
};

const TeamInline = () => (
  <TeamList>
    {TEAM_MEMBERS.map(m => (
      <TeamRow key={m.name}>
        <Avatar src={m.photo} alt={m.name} />
        <TeamInfo>
          <TeamName>{m.name}</TeamName>
          <TeamBio>{m.bio}</TeamBio>
          <TeamSiteLink href={m.website} target="_blank" rel="noopener noreferrer">
            {m.website.replace(/^https?:\/\//, '').replace(/\/$/, '')}
          </TeamSiteLink>
        </TeamInfo>
      </TeamRow>
    ))}
  </TeamList>
);

export const Landing = () => {
  const { weeks, isLoading } = useCalendarData();

  if (isLoading) {
    return <LoadingWrapper>Loading…</LoadingWrapper>;
  }

  const allDays = (weeks || []).flatMap((w: any) => w.days || []);

  return (
    <PageWrapper>
      <PageHeader>
        <PageTitle>Code for Humanities</PageTitle>
      </PageHeader>
      <Shell>
        <Sidebar aria-label="Table of contents">
          <SidebarLink href="#about">About</SidebarLink>
          {allDays.map((day: any, i: number) => (
            <SidebarLink key={day.date || i} href={`#day-${i + 1}`} title={`Day ${i + 1} · ${DAYS[i]?.title ?? ''}`}>
              Day {i + 1} · {DAYS[i]?.title ?? ''}
            </SidebarLink>
          ))}
          <SidebarLink href="#resources">Resources</SidebarLink>
          <SidebarLink href="#team">Teaching Team</SidebarLink>
          <SidebarLink href="#thanks">Thank you!</SidebarLink>
        </Sidebar>

        <Content>
          <Section id="about">
            <SectionHeading>About</SectionHeading>
            <AboutParagraph>{ABOUT_COPY}</AboutParagraph>
          </Section>

          {allDays.map((day: any, i: number) => {
            const dayN = i + 1;
            const title = DAYS[i]?.title ?? `Day ${dayN}`;
            const supplements = SUPPLEMENTS[dayN] || [];
            const events = [
              ...(day.events || []),
              ...supplements,
            ].sort(
              (a: CalendarEvent, b: CalendarEvent) =>
                resolveEventHour(a, dayN) - resolveEventHour(b, dayN),
            );
            return (
              <Section key={day.date || i} id={`day-${dayN}`}>
                <SectionHeading>Day {dayN} · {title}</SectionHeading>
                <EventList>
                  {events.map((event: CalendarEvent, ei: number) => (
                    <EventItem key={`${event.id ?? 'e'}-${ei}`} event={event} dayN={dayN} />
                  ))}
                </EventList>
              </Section>
            );
          })}

          <Section id="resources">
            <SectionHeading>Resources</SectionHeading>
            <ResourcesList>
              {RESOURCE_LINKS.map(r => (
                <a key={r.href} href={r.href} target="_blank" rel="noopener noreferrer">
                  {r.label}
                </a>
              ))}
            </ResourcesList>
          </Section>

          <Section id="team">
            <SectionHeading>Teaching Team</SectionHeading>
            <TeamInline />
          </Section>

          <Section id="thanks">
            <SectionHeading>Thank you!</SectionHeading>
            <ThanksParagraph>
              We would like to thank Chris Piech, the Code in Place team, Nick Parlante (creator of Codingbat), and Nifty Assignments, for all of the wonderful assignments that you have given the world! Many of the assignments in this course are created by or inspired by you.
            </ThanksParagraph>
            <ThanksParagraph>
              And the biggest thanks to the wonderful humanities faculty who took this course. You were the most dedicated and enthusiastic students we could ever ask for!
            </ThanksParagraph>
            <CohortPhoto src="/people/cohort.jpeg" alt="The Code for Humanities cohort" />
            <Anecdote>
              &ldquo;Your class helped me learn to learn in a new way, and it made me feel life was richer. It's rare to experience learning that changes how you think. I wish everyone would learn to code and learn to think more clearly, creatively, and structurally.&rdquo;
            </Anecdote>
            <Anecdote>
              &ldquo;What you've done for us as researchers is huge, not just unlocking efficiency, but empowerment.&rdquo;
            </Anecdote>
          </Section>
        </Content>
      </Shell>
    </PageWrapper>
  );
};

const PageWrapper = styled.div`
  background-color: ${COLORS.bg};
  color: ${COLORS.ink};
  font-family: ${FONT.sans};
  min-height: 100vh;
`;

const LoadingWrapper = styled.div`
  padding: ${SPACE.xl};
  color: ${COLORS.mutedInk};
`;

const PageHeader = styled.header`
  max-width: 1080px;
  margin: 0 auto;
  padding: ${SPACE.xl};

  @media (max-width: ${BREAKPOINT.narrow}) {
    padding: ${SPACE.lg};
  }
`;

const Shell = styled.div`
  display: grid;
  grid-template-columns: 240px 1fr;
  max-width: 1080px;
  margin: 0 auto;
  gap: ${SPACE.lg};
  padding: 0 ${SPACE.xl} ${SPACE.xl};

  @media (max-width: ${BREAKPOINT.narrow}) {
    grid-template-columns: 1fr;
    padding: 0 ${SPACE.lg} ${SPACE.lg};
  }
`;

const Sidebar = styled.nav`
  position: sticky;
  top: ${SPACE.xl};
  align-self: start;
  max-height: calc(100vh - ${SPACE.xl});
  overflow-y: auto;
  font-family: ${FONT.sans};
  padding-right: ${SPACE.md};

  @media (max-width: ${BREAKPOINT.narrow}) {
    display: none;
  }
`;

const SidebarLink = styled.a`
  display: block;
  padding: 0.3rem 0;
  color: ${COLORS.mutedInk};
  text-decoration: none;
  font-weight: 400;
  font-size: 0.875rem;
  line-height: 1.4;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &:hover {
    color: ${COLORS.accent};
  }
`;

const Content = styled.main`
  min-width: 0;
`;

const PageTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  margin: 0;
  color: ${COLORS.ink};
  border-bottom: 3px solid ${COLORS.accent};
  padding-bottom: ${SPACE.sm};
  letter-spacing: -0.01em;
`;

const Section = styled.section`
  margin-bottom: ${SPACE.xxl};
  scroll-margin-top: ${SPACE.md};
`;

const SectionHeading = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0 0 ${SPACE.md};
`;

const AboutParagraph = styled.p`
  line-height: 1.6;
  font-size: 1rem;
  margin: 0;
  white-space: pre-line;
`;

const EventList = styled.div`
  display: flex;
  flex-direction: column;
`;

const EventRow = styled.div`
  display: grid;
  grid-template-columns: 90px 1fr auto;
  gap: ${SPACE.md};
  padding: 0.45rem ${SPACE.sm};
  align-items: baseline;
  border-radius: 4px;

  &:hover {
    background-color: ${COLORS.softBg};
  }

  @media (max-width: ${BREAKPOINT.narrow}) {
    grid-template-columns: 1fr;
    gap: 0.15rem;
    padding: 0.5rem ${SPACE.sm};
    margin-bottom: 0.25rem;
  }
`;

const BreakRow = styled.div`
  display: grid;
  grid-template-columns: 90px 1fr;
  gap: ${SPACE.md};
  padding: 0.45rem ${SPACE.sm};
  color: ${COLORS.mutedInk};
  font-style: italic;

  @media (max-width: ${BREAKPOINT.narrow}) {
    grid-template-columns: 80px 1fr;
  }
`;

const Time = styled.div`
  font-size: 0.85rem;
  color: ${COLORS.mutedInk};
  font-variant-numeric: tabular-nums;

  @media (max-width: ${BREAKPOINT.narrow}) {
    font-size: 0.78rem;
    letter-spacing: 0.02em;
  }
`;

const BreakLabel = styled.div`
  font-size: 0.95rem;
`;

const NoteRow = styled.div`
  display: grid;
  grid-template-columns: 90px 1fr;
  gap: ${SPACE.md};
  padding: 0.45rem ${SPACE.sm};
  color: ${COLORS.mutedInk};
  border-left: 3px solid ${COLORS.accent};
  background-color: ${COLORS.softBg};
  margin: ${SPACE.xs} 0;

  @media (max-width: ${BREAKPOINT.narrow}) {
    grid-template-columns: 80px 1fr;
  }
`;

const NoteLabel = styled.div`
  font-size: 0.95rem;
  font-weight: 500;
`;

const EventBody = styled.div`
  min-width: 0;
`;

const EventTitleLine = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${SPACE.xs};
  align-items: baseline;
`;

const EventTitle = styled.span`
  font-size: 0.95rem;
`;

const Pill = styled.span`
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 0.8rem;
  color: ${COLORS.ink};
  white-space: nowrap;
`;

const EventLinks = styled.div`
  display: flex;
  gap: ${SPACE.sm};
  flex-wrap: wrap;
  align-items: center;
  justify-self: end;

  @media (max-width: ${BREAKPOINT.narrow}) {
    justify-self: start;
  }
`;

const InlineLink = styled.a`
  font-size: 0.85rem;
  color: ${COLORS.accent};
  text-decoration: none;
  white-space: nowrap;

  &:hover {
    text-decoration: underline;
  }
`;

const ResourcesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACE.sm};

  a {
    color: ${COLORS.accent};
    text-decoration: none;
    font-size: 1rem;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const TeamList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACE.lg};
`;

const TeamRow = styled.div`
  display: flex;
  gap: ${SPACE.md};
  align-items: center;
`;

const Avatar = styled.img`
  width: 96px;
  height: 96px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
`;

const TeamInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const TeamName = styled.div`
  font-size: 1rem;
  font-weight: 600;
`;

const TeamBio = styled.div`
  font-size: 0.9rem;
  color: ${COLORS.mutedInk};
`;

const TeamSiteLink = styled.a`
  font-size: 0.85rem;
  color: ${COLORS.accent};
  text-decoration: none;
  word-break: break-all;

  &:hover {
    text-decoration: underline;
  }
`;

const ThanksParagraph = styled.p`
  line-height: 1.6;
  font-size: 1rem;
  margin: 0 0 ${SPACE.md};
`;

const CohortPhoto = styled.img`
  width: 100%;
  aspect-ratio: 5 / 3;
  object-fit: cover;
  object-position: center bottom;
  border-radius: 8px;
  margin: ${SPACE.md} 0 ${SPACE.lg};
  display: block;
`;

const Anecdote = styled.blockquote`
  margin: 0 0 ${SPACE.md};
  padding: ${SPACE.sm} ${SPACE.md};
  border-left: 3px solid ${COLORS.accent};
  color: ${COLORS.mutedInk};
  font-style: italic;
  line-height: 1.6;
  font-size: 1rem;
`;
